import React, { useState } from 'react';
import { EyeIcon } from '@heroicons/react/24/outline';
import { checkFakeNews } from '../api/insightsApi';
import { getCluster, getCredibility } from '../utils/newsClusters';

const NewsCard = ({ article, showSymbol = true, watchlist = false }) => {
  const [showFakeCheck, setShowFakeCheck] = useState(false);
  const [fakeNewsResult, setFakeNewsResult] = useState(null);
  const [checking, setChecking] = useState(false);

  const sentiment = article?.sentiment || {};
  const sentimentScore = sentiment.score || 0;
  const sentimentLabel = sentiment.label || 'neutral';

  // Cluster + credibility tags (guard for older cached articles without them)
  const cluster = article?.category ? getCluster(article.category) : null;
  const credTier = article?.credibility?.tier;
  const credibility = credTier ? getCredibility(credTier) : null;
  const sources = article?.corroboration_count || 0;
  
  // Sentiment badge color
  const getSentimentColor = () => {
    if (sentimentLabel === 'positive') return 'bg-green-100 text-green-800';
    if (sentimentLabel === 'negative') return 'bg-red-100 text-red-800';
    return 'bg-yellow-100 text-yellow-800';
  };

  // Format date
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    } catch {
      return 'Recent';
    }
  };

  // Handle fake news check
  const handleFakeNewsCheck = async () => {
    if (fakeNewsResult) {
      setShowFakeCheck(!showFakeCheck);
      return;
    }

    setChecking(true);
    try {
      const result = await checkFakeNews({
        title: article.title,
        description: article.description,
        source: article.source
      });
      setFakeNewsResult(result);
      setShowFakeCheck(true);
    } catch (error) {
      console.error('Fake news check failed:', error);
      setFakeNewsResult({
        credibility_score: 50,
        is_suspicious: false,
        red_flags: ['Check unavailable'],
        reasoning: 'Unable to perform analysis'
      });
      setShowFakeCheck(true);
    } finally {
      setChecking(false);
    }
  };

  const getCredibilityColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            {showSymbol && article.symbol && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-semibold rounded">
                {article.symbol}
              </span>
            )}
            {watchlist && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                <EyeIcon className="w-3.5 h-3.5" /> Watching
              </span>
            )}
            <span className={`px-2 py-0.5 text-xs font-medium rounded ${getSentimentColor()}`}>
              {sentimentLabel} {sentimentScore > 0 ? '+' : ''}{sentimentScore.toFixed(2)}
            </span>
            {cluster && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${cluster.color}`}>
                <cluster.Icon className="w-3.5 h-3.5" /> {cluster.label}
              </span>
            )}
            {credibility && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded ${credibility.color}`}>
                <credibility.Icon className="w-3.5 h-3.5" /> {credibility.label}{sources >= 2 ? ` · ${sources} sources` : ''}
              </span>
            )}
          </div>
          <h3 className="font-semibold text-gray-900 dark:text-white text-base leading-tight">
            {article.title}
          </h3>
        </div>
        {article.image_url && (
          <img 
            src={article.image_url} 
            alt="" 
            className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded ml-3 flex-shrink-0"
            onError={(e) => e.target.style.display = 'none'}
          />
        )}
      </div>

      {/* Description */}
      <p className="text-gray-600 dark:text-gray-300 text-sm mb-3 line-clamp-3">
        {article.description}
      </p>

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-3">
          <span className="font-medium">{article.source}</span>
          <span>{formatDate(article.published_at)}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleFakeNewsCheck}
            disabled={checking}
            className="px-2 py-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-xs font-medium transition-colors disabled:opacity-50"
          >
            {checking ? 'Checking...' : fakeNewsResult ? 'View Check' : 'Fake Check'}
          </button>
          <a
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            className="px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded text-xs font-medium transition-colors"
          >
            Read More →
          </a>
        </div>
      </div>

      {/* Fake News Check Results */}
      {showFakeCheck && fakeNewsResult && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-sm dark:text-white">Credibility Analysis</span>
              <span className={`text-lg font-bold ${getCredibilityColor(fakeNewsResult.credibility_score)}`}>
                {fakeNewsResult.credibility_score}/100
              </span>
            </div>
            
            {fakeNewsResult.is_suspicious && (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mb-2">
                <div className="text-yellow-800 text-xs font-medium">⚠️ Suspicious Content Detected</div>
              </div>
            )}

            {fakeNewsResult.red_flags && fakeNewsResult.red_flags.length > 0 && (
              <div className="mb-2">
                <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Red Flags:</div>
                <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
                  {fakeNewsResult.red_flags.map((flag, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-red-500 mr-1">•</span>
                      <span>{flag}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-xs text-gray-600 dark:text-gray-400 italic">
              {fakeNewsResult.reasoning}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsCard;
